import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { TradePosition, OptionHistoryEntry, ImpliedVolatility, BrokerProfile, PutHistoryEntry, RollHistoryEntry, DividendRecord } from '@covered-calls/shared';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { RollDialog } from './RollDialog';
import { ResaleDialog } from './ResaleDialog';
import { PutOutcomeDialog } from './PutOutcomeDialog';
import { ManageCloseDialog } from './ManageCloseDialog';
import { AddProtectivePutDialog } from './AddProtectivePutDialog';
import { AddDividendDialog } from './AddDividendDialog';
import { EditDividendDialog } from './EditDividendDialog';
import { calculatePnLBreakdown, shouldShowExitAlert, calculateCumulativePremiumIncome, selectSchedule, computeCommission, InstrumentClass, RollType, roundTo2dp } from '@covered-calls/shared';
import type { PnLBreakdown, TradeReport } from '@covered-calls/shared';
import { TradeReportLabel } from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';
import { getPutHistory } from '../services/protective-put-service';
import { getRollHistory } from '../services/roll-service';
import { getImpliedVolatility } from '../services/market-data-service';
import { getDividends, deleteDividend } from '../services/dividend-service';
import {
  requestReportUpload,
  uploadReportToS3,
  confirmReportUpload,
  listReports,
  getReportViewUrl,
  deleteReport,
} from '../services/trade-reports-service';
import { getIbkrConnection } from '../services/ibkr-settings-service';
import { Tabs, type TabDescriptor } from './Tabs';

/**
 * PositionDetail shows full trade details, P&L breakdown, market prices,
 * IV, exit alerts, price update functionality, and cumulative premium income.
 *
 * Validates: Requirements 4.2, 4.3, 4.7, 5.1, 5.3, 5.4, 6.1, 6.2, 9.2, 9.3, 9.5, 15.2
 */

interface PriceUpdateResponse {
  lastStockPrice: number;
  lastOptionPrice?: number;
  lastPriceTimestamp: string;
  unrealisedPnL?: number;
}

interface ManualPriceForm {
  stockPrice: string;
  optionBid: string;
  optionAsk: string;
  impliedVolatility: string;
}

const DEFAULT_EXIT_THRESHOLD = 10;

export function PositionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [position, setPosition] = useState<TradePosition | null>(null);
  const [optionHistory, setOptionHistory] = useState<OptionHistoryEntry[]>([]);
  const [putHistory, setPutHistory] = useState<PutHistoryEntry[]>([]);
  const [rollHistory, setRollHistory] = useState<RollHistoryEntry[]>([]);
  const [dividends, setDividends] = useState<DividendRecord[]>([]);
  // Surfaced (not swallowed) so a load failure shows an error and suppresses the
  // dividend total per Req 5.5.
  const [dividendsError, setDividendsError] = useState<boolean>(false);
  const [showAddDividendDialog, setShowAddDividendDialog] = useState(false);
  const [editingDividend, setEditingDividend] = useState<DividendRecord | null>(null);
  const [showPutOutcomeDialog, setShowPutOutcomeDialog] = useState(false);
  const [showManageCloseDialog, setShowManageCloseDialog] = useState(false);
  const [showAddPutDialog, setShowAddPutDialog] = useState(false);
  const [impliedVolatility, setImpliedVolatility] = useState<ImpliedVolatility | null>(null);
  const [exitThreshold, setExitThreshold] = useState<number>(DEFAULT_EXIT_THRESHOLD);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPriceUpdating, setIsPriceUpdating] = useState(false);
  // Whether automated 5-min price fetching is enabled. When on, manual price
  // controls are hidden because the scheduled fetcher keeps prices current.
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | undefined>(undefined);
  const [refreshFailureCount, setRefreshFailureCount] = useState(0);
  const [priceUpdateError, setPriceUpdateError] = useState<string | null>(null);
  // Selected tab for the Position Detail tabbed sections. Initialised to the
  // Market Data tab and never persisted — nothing is read from or written to
  // localStorage, the URL, or any store, so selection always resets to Market
  // Data on every mount/remount (Req 6.1/6.2/6.3).
  const [selectedTabId, setSelectedTabId] = useState<string>('market-data');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualForm, setManualForm] = useState<ManualPriceForm>({ stockPrice: '', optionBid: '', optionAsk: '', impliedVolatility: '' });
  const [manualFormError, setManualFormError] = useState<string | null>(null);
  const [showRollDialog, setShowRollDialog] = useState(false);
  const [showResaleDialog, setShowResaleDialog] = useState(false);
  const [brokerProfiles, setBrokerProfiles] = useState<BrokerProfile[]>([]);

  // Option quotes for rolling strategy analysis
  interface OptionQuote {
    expiry: string;
    strike: number;
    bid: string;
    ask: string;
  }
  const [optionQuotes, setOptionQuotes] = useState<OptionQuote[]>([
    { expiry: '', strike: 0, bid: '', ask: '' },
    { expiry: '', strike: 0, bid: '', ask: '' },
  ]);
  const [showQuoteEntry, setShowQuoteEntry] = useState(false);
  const [selectedRollStrategy, setSelectedRollStrategy] = useState<{ strategy: string; rollType?: RollType; strike?: number; expiry?: string; premium?: number } | null>(null);

  // Load persisted option quotes from position
  useEffect(() => {
    if (position?.optionQuotes && position.optionQuotes.length > 0) {
      const loaded: OptionQuote[] = position.optionQuotes.map(q => ({
        expiry: q.expiry,
        strike: q.strike,
        bid: q.bid.toString(),
        ask: q.ask.toString(),
      }));
      // Pad to minimum 3 rows
      while (loaded.length < 2) {
        loaded.push({ expiry: '', strike: 0, bid: '', ask: '' });
      }
      setOptionQuotes(loaded);
    }
  }, [position?.optionQuotes]);

  // --- Tabbed sections: availability predicates, tab model, fallback ---------
  // These mirror today's inline section render conditions verbatim (see below).
  // They are computed here (ahead of the loading/error/!position early returns)
  // so the reconciliation effect that follows can be an unconditional hook and
  // not violate the Rules of Hooks. Each predicate is `false` while `position`
  // is still null, which is harmless — nothing consumes `tabs`/`effectiveTabId`
  // until `position` is loaded. When `position` is non-null these reduce to the
  // exact conditions quoted in the design:
  //   ivAvailable      = position.ivPercentile !== undefined && position.status === 'OPEN'
  //   rollingAvailable = position.status === 'OPEN'
  //                      && position.currentOption?.status === 'ACTIVE'
  //                      && position.lastStockPrice !== undefined
  const ivAvailable =
    position?.ivPercentile !== undefined && position?.status === 'OPEN';
  const rollingAvailable =
    position?.status === 'OPEN' &&
    position?.currentOption?.status === 'ACTIVE' &&
    position?.lastStockPrice !== undefined;

  // Panel render functions — each returns the exact JSX that previously lived
  // inline in the single return tree, moved verbatim into a tab panel. They are
  // inline closures over `position`, `optionQuotes`, the handlers, etc. (no new
  // props), and are only invoked by <Tabs> during render — i.e. after the
  // loading/error/!position early returns below — so `position` is non-null when
  // they run. `position!` here is a type-narrowing assertion only (predicates
  // above already guarantee availability); it introduces no behaviour change.
  const p = position!;

  const renderMarketDataPanel = () => (
    <section aria-labelledby="market-data-heading" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
        <h3 id="market-data-heading" style={{ margin: 0 }}>
          Market Data
        </h3>
        <DataSourceBadge isPriceManual={p.isPriceManual} lastPriceTimestamp={p.lastPriceTimestamp} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        <DetailItem
          label="Last Stock Price"
          value={p.lastStockPrice !== undefined ? `$${formatPrice(p.lastStockPrice)}` : 'N/A'}
        />
        <DetailItem
          label="Option Bid (sell)"
          value={p.lastOptionBid !== undefined ? `$${formatPrice(p.lastOptionBid)}` : 'N/A'}
        />
        <DetailItem
          label="Option Ask (buy)"
          value={p.lastOptionAsk !== undefined ? `$${formatPrice(p.lastOptionAsk)}` : 'N/A'}
        />
        <DetailItem
          label="Option Mid"
          value={p.lastOptionPrice !== undefined ? `$${formatPrice(p.lastOptionPrice)}` : 'N/A'}
        />
        <DetailItem
          label="Last Updated"
          value={p.lastPriceTimestamp ? formatRelativeTime(p.lastPriceTimestamp) : 'Never'}
        />
        {p.isPriceManual && (
          <DetailItem label="Price Source" value="Manual Entry" />
        )}

        {(impliedVolatility || p.lastImpliedVolatility !== undefined) && (
          <DetailItem
            label="Implied Volatility"
            value={`${(impliedVolatility?.value ?? p.lastImpliedVolatility ?? 0).toFixed(2)}%`}
          />
        )}
      </div>

      {/* Auto-refresh status indicators — shown only when auto-refresh is active */}
      {autoRefreshEnabled && lastRefreshedAt && (
        <span
          aria-live="polite"
          aria-atomic="true"
          style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}
        >
          Last updated: {lastRefreshedAt.toLocaleTimeString()}
        </span>
      )}
      {autoRefreshEnabled && refreshFailureCount >= 3 && (
        <div role="status" style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
          Prices could not be refreshed. Displaying last known data.
        </div>
      )}

      {/* Price update controls — hidden when automated fetching is enabled,
          since the scheduled fetcher keeps prices current. */}
      {p.status === 'OPEN' && !autoRefreshEnabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={handlePriceUpdate}
            disabled={isPriceUpdating}
            aria-busy={isPriceUpdating}
            style={{
              padding: '0.5rem 1.25rem',
              backgroundColor: isPriceUpdating ? 'var(--color-muted)' : 'var(--color-brand)',
              color: 'var(--color-on-brand)',
              border: 'none',
              borderRadius: '4px',
              cursor: isPriceUpdating ? 'not-allowed' : 'pointer',
              minHeight: '44px',
              width: 'fit-content',
              opacity: isPriceUpdating ? 0.7 : 1,
            }}
          >
            {isPriceUpdating ? 'Updating...' : 'Update Prices'}
          </button>
          <button
            onClick={() => {
              setManualForm({
                stockPrice: p.lastStockPrice?.toString() ?? '',
                optionBid: p.lastOptionBid?.toString() ?? '',
                optionAsk: p.lastOptionAsk?.toString() ?? '',
                impliedVolatility: p.lastImpliedVolatility?.toString() ?? '',
              });
              setShowManualEntry(true);
            }}
            disabled={isPriceUpdating}
            style={{
              padding: '0.5rem 1.25rem',
              backgroundColor: 'transparent',
              color: 'var(--color-brand)',
              border: '1px solid var(--color-brand)',
              borderRadius: '4px',
              cursor: 'pointer',
              minHeight: '44px',
              width: 'fit-content',
            }}
          >
            Enter Manually
          </button>
          </div>

          {/* Price update error */}
          {priceUpdateError && (
            <div role="alert" style={{ padding: '0.75rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', fontSize: '0.875rem' }}>
              {priceUpdateError}
            </div>
          )}

          {/* Manual price entry fallback */}
          {showManualEntry && (
            <form onSubmit={handleManualPriceSubmit} style={{ padding: '1rem', backgroundColor: 'var(--color-surface-2)', borderRadius: '4px', border: '1px solid var(--color-border)', maxWidth: '700px' }}>
              <h4 style={{ margin: '0 0 0.75rem 0' }}>Enter Prices Manually</h4>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div>
                  <label htmlFor="manual-stock-price" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Stock Price *
                  </label>
                  <input
                    id="manual-stock-price"
                    type="number"
                    step="0.001"
                    min="0.01"
                    value={manualForm.stockPrice}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, stockPrice: e.target.value }))}
                    required
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px' }}
                    placeholder="e.g. 150.00"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label htmlFor="manual-option-bid" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                      Option Bid (sell)
                    </label>
                    <input
                      id="manual-option-bid"
                      type="number"
                      step="0.001"
                      min="0.01"
                      value={manualForm.optionBid}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, optionBid: e.target.value }))}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', boxSizing: 'border-box' }}
                      placeholder="e.g. 0.340"
                    />
                  </div>
                  <div>
                    <label htmlFor="manual-option-ask" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                      Option Ask (buy)
                    </label>
                    <input
                      id="manual-option-ask"
                      type="number"
                      step="0.001"
                      min="0.01"
                      value={manualForm.optionAsk}
                      onChange={(e) => setManualForm((prev) => ({ ...prev, optionAsk: e.target.value }))}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', boxSizing: 'border-box' }}
                      placeholder="e.g. 0.380"
                    />
                  </div>
                </div>
                {manualForm.optionBid && manualForm.optionAsk && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                    Mid price: ${(((parseFloat(manualForm.optionBid) || 0) + (parseFloat(manualForm.optionAsk) || 0)) / 2).toFixed(3)}
                  </p>
                )}
                <div style={{ marginTop: '0.5rem' }}>
                  <label htmlFor="manual-iv" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
                    Implied Volatility % (optional)
                  </label>
                  <input
                    id="manual-iv"
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualForm.impliedVolatility}
                    onChange={(e) => setManualForm((prev) => ({ ...prev, impliedVolatility: e.target.value }))}
                    style={{ width: '100%', maxWidth: '120px', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', boxSizing: 'border-box' }}
                    placeholder="e.g. 25.5"
                  />
                </div>
                {/* Option Quotes for Rolling Analysis */}
                {p.currentOption?.status === 'ACTIVE' && (
                  <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.875rem' }}>
                      Option Quotes (for rolling analysis)
                    </label>
                    <p style={{ fontSize: '0.75rem', color: 'var(--color-muted)', margin: '0 0 0.5rem' }}>
                      Enter bid/ask for potential roll targets. Saved with market data.
                    </p>
                    {/* Column headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)', fontWeight: 600 }}>Expiry</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)', fontWeight: 600 }}>Strike ($)</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)', fontWeight: 600 }}>Bid (sell)</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-muted)', fontWeight: 600 }}>Ask (buy)</span>
                    </div>
                    {optionQuotes.map((quote, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.375rem' }}>
                        <input type="date" value={quote.expiry} onChange={(e) => { const q = [...optionQuotes]; q[idx] = {...q[idx], expiry: e.target.value}; setOptionQuotes(q); }} style={{ width: '100%', padding: '0.375rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }} />
                        <input type="number" step="0.01" value={quote.strike || ''} onChange={(e) => { const q = [...optionQuotes]; q[idx] = {...q[idx], strike: parseFloat(e.target.value) || 0}; setOptionQuotes(q); }} style={{ width: '100%', padding: '0.375rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }} placeholder="0.00" />
                        <input type="number" step="0.001" value={quote.bid} onChange={(e) => { const q = [...optionQuotes]; q[idx] = {...q[idx], bid: e.target.value}; setOptionQuotes(q); }} style={{ width: '100%', padding: '0.375rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }} placeholder="0.000" />
                        <input type="number" step="0.001" value={quote.ask} onChange={(e) => { const q = [...optionQuotes]; q[idx] = {...q[idx], ask: e.target.value}; setOptionQuotes(q); }} style={{ width: '100%', padding: '0.375rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', fontSize: '0.8rem', boxSizing: 'border-box' }} placeholder="0.000" />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                      <button type="button" onClick={() => setOptionQuotes([...optionQuotes, { expiry: '', strike: 0, bid: '', ask: '' }])} style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', background: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '3px', cursor: 'pointer', minHeight: 'auto' }}>+ Add</button>
                      {optionQuotes.length > 2 && <button type="button" onClick={() => setOptionQuotes(optionQuotes.slice(0, -1))} style={{ padding: '0.2rem 0.5rem', fontSize: '0.7rem', background: 'transparent', color: 'var(--color-danger)', border: '1px solid var(--color-danger)', borderRadius: '3px', cursor: 'pointer', minHeight: 'auto' }}>Remove</button>}
                    </div>
                  </div>
                )}

                {manualFormError && (
                  <div role="alert" style={{ color: 'var(--color-danger)', fontSize: '0.8125rem' }}>
                    {manualFormError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isPriceUpdating}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'var(--color-success)',
                    color: 'var(--color-on-brand)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isPriceUpdating ? 'not-allowed' : 'pointer',
                    minHeight: '44px',
                  }}
                >
                  {isPriceUpdating ? 'Saving...' : 'Save Manual Prices'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowManualEntry(false); setManualFormError(null); }}
                  disabled={isPriceUpdating}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: 'transparent',
                    color: 'var(--color-muted)',
                    border: '1px solid var(--color-muted)',
                    borderRadius: '4px',
                    cursor: isPriceUpdating ? 'not-allowed' : 'pointer',
                    minHeight: '44px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </section>
  );

  const renderIvPanel = () => (
    <section aria-labelledby="iv-percentile-heading" style={{ marginBottom: '1.5rem' }}>
      <h3 id="iv-percentile-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        IV Environment
        <IvInfoButton />
      </h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--color-muted)', marginBottom: '0.75rem' }}>
        {p.ivPercentile! >= 70
          ? 'Premiums are rich — good time to sell or roll options. Consider further OTM strikes.'
          : p.ivPercentile! >= 40
          ? 'Premiums are fairly valued. Standard strike selection applies.'
          : 'Premiums are cheap — consider waiting for IV to expand, or sell closer to the money.'}
      </p>
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', display: 'block' }}>IV Percentile</span>
          <span style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: p.ivPercentile! >= 70 ? 'var(--color-success)' : p.ivPercentile! >= 40 ? 'var(--color-warning)' : 'var(--color-danger)',
          }}>
            {p.ivPercentile}%
          </span>
        </div>
        {p.iv52WeekHigh !== undefined && (
          <div>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', display: 'block' }}>52-Wk High</span>
            <span style={{ fontSize: '1rem', fontWeight: 600 }}>{p.iv52WeekHigh.toFixed(1)}%</span>
          </div>
        )}
        {p.iv52WeekLow !== undefined && (
          <div>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', display: 'block' }}>52-Wk Low</span>
            <span style={{ fontSize: '1rem', fontWeight: 600 }}>{p.iv52WeekLow.toFixed(1)}%</span>
          </div>
        )}
        {p.lastImpliedVolatility !== undefined && (
          <div>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', display: 'block' }}>Current IV</span>
            <span style={{ fontSize: '1rem', fontWeight: 600 }}>{p.lastImpliedVolatility.toFixed(1)}%</span>
          </div>
        )}
      </div>
      {/* Visual percentile bar */}
      <div style={{ marginTop: '0.75rem', maxWidth: '300px' }}>
        <div style={{ height: '6px', backgroundColor: 'var(--color-surface-2)', borderRadius: '3px' }}>
          <div style={{
            height: '100%',
            width: `${p.ivPercentile}%`,
            backgroundColor: p.ivPercentile! >= 70 ? 'var(--color-success)' : p.ivPercentile! >= 40 ? 'var(--color-warning)' : 'var(--color-danger)',
            borderRadius: '3px',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: 'var(--color-muted)', marginTop: '0.2rem' }}>
          <span>Cheap premiums</span>
          <span>Rich premiums</span>
        </div>
      </div>
    </section>
  );

  const renderRollingPanel = () => (
    <>
      <RollingGuidance
        stockPrice={p.lastStockPrice!}
        strikePrice={p.currentOption!.strikePrice}
        premium={p.currentOption!.premium}
        expiryDate={p.currentOption!.expiryDate}
        optionPrice={p.lastOptionPrice}
        entryPrice={p.purchasePrice}
        optionQuotes={optionQuotes.filter(q => q.expiry && parseFloat(q.bid) > 0).map(q => ({ expiry: q.expiry, strike: q.strike, bid: parseFloat(q.bid), ask: parseFloat(q.ask) }))}
        quantity={p.quantity}
        commissionPerTrade={(() => {
          // Estimate the per-trade OPTIONS commission (buyback / new sale) for
          // the roll analysis from the first broker profile's schedule for this
          // position's (exchange, OPTIONS) cell. Fall back to the commission
          // already stored on the current option when no schedule applies.
          const fallback = p.currentOption!.commissionEntry;
          if (brokerProfiles.length === 0) return fallback;
          const schedule = selectSchedule(brokerProfiles[0], p.exchange, InstrumentClass.OPTIONS);
          if (!schedule) return fallback;
          return computeCommission(schedule, {
            tradeValue: p.currentOption!.strikePrice * p.quantity,
            quantity: p.quantity,
          }).total;
        })()}
        onSelectRoll={(params) => {
          setSelectedRollStrategy(params);
          setShowRollDialog(true);
        }}
      />

      {/* Option Quotes Summary (persisted from manual entry) */}
      {p.status === 'OPEN' && p.currentOption?.status === 'ACTIVE' && optionQuotes.some(q => q.expiry && parseFloat(q.bid) > 0) && (
        <section aria-labelledby="option-quotes-heading" style={{ marginBottom: '1.5rem' }}>
          <h3 id="option-quotes-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>Option Quotes (for Rolling Analysis)</h3>

          <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th style={{ padding: '0.375rem', textAlign: 'left' }}>Expiry</th>
                    <th style={{ padding: '0.375rem', textAlign: 'right' }}>Strike</th>
                    <th style={{ padding: '0.375rem', textAlign: 'right' }}>Bid (sell)</th>
                    <th style={{ padding: '0.375rem', textAlign: 'right' }}>Ask (buy)</th>
                  </tr>
                </thead>
                <tbody>
                  {optionQuotes.filter(q => q.expiry && parseFloat(q.bid) > 0).map((q, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.375rem' }}>{new Date(q.expiry).toLocaleDateString()}</td>
                      <td style={{ padding: '0.375rem', textAlign: 'right' }}>${q.strike.toFixed(2)}</td>
                      <td style={{ padding: '0.375rem', textAlign: 'right' }}>${parseFloat(q.bid).toFixed(3)}</td>
                      <td style={{ padding: '0.375rem', textAlign: 'right' }}>${parseFloat(q.ask).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </section>
      )}
    </>
  );

  const renderDividendsPanel = () => (
    <section aria-labelledby="dividends-heading" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
        <h3 id="dividends-heading" style={{ margin: 0 }}>
          Dividends
        </h3>
        <button
          onClick={() => setShowAddDividendDialog(true)}
          style={{ padding: '0.375rem 0.875rem', backgroundColor: 'var(--color-success)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8125rem', minHeight: '36px' }}
        >
          Add Dividend
        </button>
      </div>

      {dividendsError ? (
        <div role="alert" style={{ padding: '0.75rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', fontSize: '0.875rem' }}>
          Dividend history could not be loaded.
        </div>
      ) : dividends.length === 0 ? (
        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
          No dividends recorded. Total dividend income: $0.00
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '700px' }} aria-label="Dividends">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem' }}>Payment Date</th>
                <th style={{ padding: '0.5rem' }}>Per Share</th>
                <th style={{ padding: '0.5rem' }}>Amount</th>
                <th style={{ padding: '0.5rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedDividends.map((entry) => (
                <tr key={entry.dividendId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{new Date(entry.paymentDate).toLocaleDateString()}</td>
                  <td style={{ padding: '0.5rem' }}>${entry.perShare.toFixed(2)}</td>
                  <td style={{ padding: '0.5rem' }}>${entry.amount.toFixed(2)}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setEditingDividend(entry)}
                        style={{ padding: '0.25rem 0.625rem', backgroundColor: 'transparent', color: 'var(--color-accent)', border: '1px solid var(--color-accent)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', minHeight: 'auto' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDividend(entry)}
                        style={{ padding: '0.25rem 0.625rem', backgroundColor: 'transparent', color: 'var(--color-danger)', border: '1px solid var(--color-danger)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', minHeight: 'auto' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--color-border)', fontWeight: 600 }}>
                <td style={{ padding: '0.5rem' }}>Total Dividend Income</td>
                <td style={{ padding: '0.5rem' }}></td>
                <td style={{ padding: '0.5rem' }} colSpan={2}>${totalDividendIncome.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );

  const renderTradeReportsPanel = () => (
    <>{id && <TradeReports positionId={id} />}</>
  );

  // Fixed canonical order (Req 1.2 / 3.6). Market Data, Dividends and Trade
  // Reports are always present (Req 3.5); IV Environment and Rolling appear only
  // when their predicate holds (Req 3.1-3.4). Each renderPanel is wired to the
  // matching section JSX relocated above (task 4).
  const tabs: TabDescriptor[] = [
    { id: 'market-data', label: 'Market Data', renderPanel: renderMarketDataPanel },
    ...(ivAvailable
      ? [{ id: 'iv-environment', label: 'IV Environment', renderPanel: renderIvPanel }]
      : []),
    ...(rollingAvailable
      ? [{ id: 'rolling', label: 'Rolling Strategy Guidance', renderPanel: renderRollingPanel }]
      : []),
    { id: 'dividends', label: 'Dividends', renderPanel: renderDividendsPanel },
    { id: 'trade-reports', label: 'Trade Reports', renderPanel: renderTradeReportsPanel },
  ];

  // The selected id must always point at an available tab. When it does not
  // (e.g. a conditional tab the user was on became unavailable after a refetch),
  // fall back to the first available tab in canonical order — always Market Data
  // (Req 4.1-4.3).
  const effectiveTabId = tabs.some((t) => t.id === selectedTabId) ? selectedTabId : tabs[0].id;

  // Keep the stored id in sync with the effective id so the roving tabindex in
  // the (task 4) <Tabs> DOM stays consistent. Rendering should use
  // `effectiveTabId` directly so the correct panel shows on the first render
  // after availability changes, without waiting for this effect.
  useEffect(() => {
    if (effectiveTabId !== selectedTabId) {
      setSelectedTabId(effectiveTabId);
    }
  }, [effectiveTabId, selectedTabId]);

  const fetchPosition = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<TradePosition>(`/trades/${id}`);
      setPosition(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load position details.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const fetchOptionHistory = useCallback(async () => {
    if (!id) return;
    try {
      const data = await apiClient.get<OptionHistoryEntry[]>(`/trades/${id}/option-history`);
      setOptionHistory(data);
    } catch {
      // Option history is supplementary; don't block the view
    }
  }, [id]);

  const fetchPutHistory = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getPutHistory(id);
      setPutHistory(data);
    } catch {
      // Put history is supplementary; don't block the view
    }
  }, [id]);

  const fetchRollHistory = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getRollHistory(id);
      setRollHistory(data);
    } catch {
      // Roll history is supplementary; don't block the view
    }
  }, [id]);

  const fetchDividends = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getDividends(id);
      setDividends(data);
      setDividendsError(false);
    } catch {
      // Unlike put history, a dividend load failure must be surfaced (Req 5.5):
      // show an error indication and suppress the Total_Dividend_Income value.
      setDividendsError(true);
    }
  }, [id]);

  const fetchImpliedVolatility = useCallback(async (ticker: string) => {
    try {
      const data = await getImpliedVolatility(ticker);
      setImpliedVolatility(data);
    } catch {
      // IV is optional; requirement 5.4 says don't block if unavailable
      setImpliedVolatility(null);
    }
  }, []);

  const fetchExitThreshold = useCallback(async () => {
    try {
      const data = await apiClient.get<{ exitAlertThreshold: number }>('/settings/exit-threshold');
      setExitThreshold(data.exitAlertThreshold);
    } catch {
      // Use default threshold if settings can't be fetched
    }
  }, []);

  const fetchBrokerProfiles = useCallback(async () => {
    try {
      const profiles = await apiClient.get<BrokerProfile[]>('/broker-profiles');
      setBrokerProfiles(profiles);
    } catch {
      // Non-critical
    }
  }, []);

  // Background refresh — does NOT set isLoading (no layout flash on periodic
  // refreshes). Only runs when autoRefreshEnabled is true.
  const backgroundRefresh = useCallback(async () => {
    if (!id) return;
    const data = await apiClient.get<TradePosition>(`/trades/${id}`);
    setPosition(data);
    setLastRefreshedAt(new Date());
  }, [id]);

  useAutoRefresh({
    fetchFn: backgroundRefresh,
    enabled: autoRefreshEnabled,
    onError: (count) => setRefreshFailureCount(count),
  });


  useEffect(() => {
    // Non-critical: on failure leave manual controls visible (disabled = false).
    let cancelled = false;
    (async () => {
      try {
        const conn = await getIbkrConnection();
        if (!cancelled) setAutoRefreshEnabled(conn?.enabled ?? false);
      } catch {
        if (!cancelled) setAutoRefreshEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchPosition();
    fetchOptionHistory();
    fetchPutHistory();
    fetchRollHistory();
    fetchDividends();
    fetchExitThreshold();
    fetchBrokerProfiles();
  }, [fetchPosition, fetchOptionHistory, fetchPutHistory, fetchRollHistory, fetchDividends, fetchExitThreshold, fetchBrokerProfiles]);

  useEffect(() => {
    if (position?.ticker && position.currentOption?.status === 'ACTIVE') {
      fetchImpliedVolatility(position.ticker);
    }
  }, [position?.ticker, position?.currentOption?.status, fetchImpliedVolatility]);


  const handlePriceUpdate = async () => {
    if (!id) return;
    setIsPriceUpdating(true);
    setPriceUpdateError(null);
    setShowManualEntry(false);
    try {
      await apiClient.post(`/market/fetch-live/${id}`);
      // Refetch the position to get the updated prices
      await fetchPosition();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setPriceUpdateError(err.message);
      } else {
        setPriceUpdateError('Price update failed. You can enter prices manually.');
      }
      setShowManualEntry(true);
    } finally {
      setIsPriceUpdating(false);
    }
  };

  const handleManualPriceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    const stockPrice = parseFloat(manualForm.stockPrice);
    const optionBid = manualForm.optionBid ? parseFloat(manualForm.optionBid) : undefined;
    const optionAsk = manualForm.optionAsk ? parseFloat(manualForm.optionAsk) : undefined;

    // Calculate mid price from bid/ask
    let optionPrice: number | undefined;
    if (optionBid !== undefined && optionAsk !== undefined) {
      optionPrice = Math.round(((optionBid + optionAsk) / 2) * 1000) / 1000;
    } else if (optionBid !== undefined) {
      optionPrice = optionBid;
    } else if (optionAsk !== undefined) {
      optionPrice = optionAsk;
    }

    // Validate positive numbers
    if (isNaN(stockPrice) || stockPrice <= 0) {
      setManualFormError('Stock price must be a positive number.');
      return;
    }
    if (optionBid !== undefined && (isNaN(optionBid) || optionBid <= 0)) {
      setManualFormError('Option bid must be a positive number.');
      return;
    }
    if (optionAsk !== undefined && (isNaN(optionAsk) || optionAsk <= 0)) {
      setManualFormError('Option ask must be a positive number.');
      return;
    }

    setManualFormError(null);
    setIsPriceUpdating(true);
    try {
      const body: { stockPrice: number; optionPrice?: number; optionBid?: number; optionAsk?: number; optionQuotes?: Array<{ expiry: string; strike: number; bid: number; ask: number }> } = { stockPrice };
      if (optionPrice !== undefined) {
        body.optionPrice = optionPrice;
      }
      if (optionBid !== undefined) {
        body.optionBid = optionBid;
      }
      if (optionAsk !== undefined) {
        body.optionAsk = optionAsk;
      }
      const iv = manualForm.impliedVolatility ? parseFloat(manualForm.impliedVolatility) : undefined;
      if (iv !== undefined && !isNaN(iv)) {
        (body as any).impliedVolatility = iv;
      }
      // Include any entered option quotes
      const validQuotes = optionQuotes
        .filter(q => q.expiry && parseFloat(q.bid) > 0)
        .map(q => ({ expiry: q.expiry, strike: q.strike, bid: parseFloat(q.bid), ask: parseFloat(q.ask) || 0 }));
      if (validQuotes.length > 0) {
        body.optionQuotes = validQuotes;
      }
      await apiClient.post(`/market/manual-price/${id}`, body);
      setPosition((prev) =>
        prev
          ? {
              ...prev,
              lastStockPrice: stockPrice,
              lastOptionPrice: optionPrice,
              lastOptionBid: optionBid,
              lastOptionAsk: optionAsk,
              lastImpliedVolatility: manualForm.impliedVolatility ? parseFloat(manualForm.impliedVolatility) : prev?.lastImpliedVolatility,
              lastPriceTimestamp: new Date().toISOString(),
              isPriceManual: true,
            }
          : prev
      );
      setShowManualEntry(false);
      setPriceUpdateError(null);
      setManualForm({ stockPrice: '', optionBid: '', optionAsk: '', impliedVolatility: '' });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setManualFormError(err.message);
      } else {
        setManualFormError('Failed to save manual prices.');
      }
    } finally {
      setIsPriceUpdating(false);
    }
  };

  // Breakeven: purchase price minus cumulative net premium per share
  const cumulativePremiumPerShare = optionHistory.length > 0
    ? optionHistory.reduce((sum, entry) => sum + entry.premium - (entry.buybackCost ?? 0), 0)
      + (position?.currentOption?.status === 'ACTIVE' ? (position.currentOption.premium ?? 0) : 0)
    : (position?.currentOption?.premium ?? 0);
  const breakeven = position ? Math.max(0, position.purchasePrice - cumulativePremiumPerShare) : undefined;

  // Exit alert check — uses breakeven (purchase price minus cumulative premiums)
  const showExitAlert =
    position &&
    position.status === 'OPEN' &&
    position.lastStockPrice !== undefined &&
    breakeven !== undefined &&
    breakeven > 0 &&
    shouldShowExitAlert(breakeven, position.lastStockPrice, exitThreshold);

  // P&L breakdown — includes cumulative premiums from option history
  const pnlBreakdown: PnLBreakdown | undefined = position
    ? calculatePnLBreakdown(position, optionHistory.length > 0 ? optionHistory : undefined, dividends)
    : undefined;

  // Display-only P&L views derived from the breakdown (no shared-calc change):
  //  - optionLiability: the active-call buyback liability line (negative/zero).
  //  - incomeToDate:    headline total EXCLUDING that liability — premium +
  //                     stock move - commissions - put + dividends. Matches the
  //                     Dashboard's Net P&L for open positions.
  //  - markToMarket:    the full mark-to-market total (income to date minus the
  //                     cost to close the open call) — the breakdown's own total.
  // Only meaningful for unrealised positions; the realised branch is unchanged.
  const optionLiability = pnlBreakdown?.netRollCostCredit ?? 0;
  const incomeToDate = pnlBreakdown ? roundTo2dp(pnlBreakdown.netProfitLoss - pnlBreakdown.netRollCostCredit) : 0;
  const markToMarket = pnlBreakdown?.netProfitLoss ?? 0;
  // Whether there is an open written call. The mark-to-market grouping is shown
  // whenever a call is active — NOT only when the liability is non-zero — so a
  // just-sold call (e.g. via the Buy Stock → Sell Option flow) still surfaces
  // the figures even before a current option price has been fetched.
  const hasActiveCall = position?.currentOption?.status === 'ACTIVE';
  // A current option price is needed to quantify the cost to close. Without one
  // (freshly sold call, no market data yet) the cost is shown as N/A rather than
  // a misleading $0.00 (which would imply a worthless call).
  const hasOptionPrice = position?.lastOptionPrice !== undefined;

  // Display-only decomposition of the unrealised Stock Gain/Loss line into the
  // capped (to-strike) portion and the above-strike portion offset by the short
  // call. Gated to the unrealised view with an active written call where the
  // stock has risen strictly above the strike; the single line is kept in every
  // other state. hasActiveCall already guarantees currentOption is ACTIVE, so
  // strikePrice is defined whenever the gate is true. The split reads only
  // stock-side fields (never lastOptionPrice/premium/protectivePut) so it stays
  // independent of the option price and the protective put.
  const strikePrice = position?.currentOption?.strikePrice;
  const lastStockPrice = position?.lastStockPrice;
  const showStockGainSplit =
    pnlBreakdown !== undefined &&
    pnlBreakdown.isRealised === false &&
    hasActiveCall &&
    strikePrice !== undefined &&
    lastStockPrice !== undefined &&
    lastStockPrice > strikePrice;
  const stockGainSplit = showStockGainSplit
    ? splitStockGain(lastStockPrice, strikePrice, position.purchasePrice, position.quantity)
    : undefined;

  // Total_Dividend_Income for the history table footer (Req 5.3). Suppressed
  // (undefined) when the dividend load failed (Req 5.5).
  const totalDividendIncome = dividends.reduce((sum, d) => sum + d.amount, 0);

  // Dividends ordered by paymentDate desc, tie-broken by amount desc (Req 5.2).
  // Sort a copy so the fetched order/state is untouched.
  const sortedDividends = [...dividends].sort((a, b) => {
    if (a.paymentDate !== b.paymentDate) {
      return a.paymentDate < b.paymentDate ? 1 : -1;
    }
    return b.amount - a.amount;
  });

  const handleDeleteDividend = async (dividend: DividendRecord) => {
    if (!id) return;
    if (!confirm('Delete this dividend record? This will remove it from the position P&L.')) return;
    try {
      await deleteDividend(id, dividend.dividendId);
      fetchPosition();
      fetchDividends();
    } catch (err) {
      if (err instanceof ApiClientError) {
        alert(err.message);
      } else {
        alert('Failed to delete dividend.');
      }
    }
  };

  // Total commission incurred at entry (all legs), derived as total minus any
  // exit-leg commissions. For a protective put this correctly includes both the
  // stock buy and the put buy; for a covered call it equals the stock entry
  // commission. Falls back to the stock entry commission when nothing is closed.
  const entryCommission = position
    ? roundTo2dp(position.totalCommissions - (position.exitCommissions ?? 0))
    : 0;

  // Cumulative premium income from option history (quantity-adjusted)
  const quantity = position?.quantity ?? 1;
  const cumulativePremium =
    optionHistory.length > 0
      ? calculateCumulativePremiumIncome(
          optionHistory.map((entry) => ({
            premium: entry.premium * quantity,
            buybackCost: entry.buybackCost !== undefined ? entry.buybackCost * quantity : undefined,
          }))
        )
      : undefined;

  if (isLoading) {
    return (
      <div aria-live="polite" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
        Loading position details...
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" style={{ padding: '1rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px' }}>
        <p>{error}</p>
        <button
          onClick={fetchPosition}
          style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!position) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>
        Position not found.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', width: '100%' }}>
      {/* Back navigation */}
      <button
        onClick={() => navigate('/trades')}
        style={{ marginBottom: '1rem', background: 'none', border: 'none', color: 'var(--color-link)', cursor: 'pointer', padding: '0.25rem 0', fontSize: '1rem', minHeight: 'auto', textDecoration: 'underline' }}
      >
        &larr; Back to positions
      </button>

      {/* Exit alert notification */}
      {showExitAlert && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            padding: '1rem',
            backgroundColor: 'var(--color-chip-warning-bg)',
            color: 'var(--color-chip-warning-text)',
            border: '1px solid var(--color-warning)',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
        >
          <strong>Exit Alert:</strong> The stock price has dropped more than {exitThreshold}% below your breakeven price (${breakeven?.toFixed(2)}). Consider exiting this position to limit losses.
        </div>
      )}

      {/* Position header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{position.ticker}</h2>
        <span
          style={{
            display: 'inline-block',
            padding: '0.125rem 0.5rem',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: position.status === 'OPEN' ? 'var(--color-chip-success-bg)' : 'var(--color-surface-2)',
            color: position.status === 'OPEN' ? 'var(--color-chip-success-text)' : 'var(--color-muted)',
          }}
        >
          {position.status}
        </span>
        <span style={{ color: 'var(--color-muted)', fontSize: '0.875rem' }}>
          {position.tradeType === 'BUY_WRITE'
            ? 'Buy/Write'
            : position.tradeType === 'PROTECTIVE_PUT'
              ? 'Protective Put'
              : 'Separate Transaction'}
        </span>
      </div>

      {/* Action buttons */}
      {position.status === 'OPEN' && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          {(!position.currentOption || position.currentOption.status !== 'ACTIVE') && (
            <button
              onClick={() => setShowResaleDialog(true)}
              style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-success)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}
            >
              Sell New Option
            </button>
          )}
          {/* Attach a protective put to this holding (separate-transaction path).
              Shown while the position is OPEN and has no active protective put;
              coexists with any covered call. */}
          {(!position.protectivePut || position.protectivePut.status !== 'ACTIVE') && (
            <button
              onClick={() => setShowAddPutDialog(true)}
              style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-accent)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}
            >
              Add Protective Put
            </button>
          )}
          <button
            onClick={() => setShowManageCloseDialog(true)}
            style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}
          >
            Manage/Close Position
          </button>
        </div>
      )}

      {/* Dialogs */}
      {showRollDialog && position.currentOption && (
        <RollDialog
          positionId={position.positionId}
          currentStrike={position.currentOption.strikePrice}
          currentExpiry={position.currentOption.expiryDate}
          ticker={position.ticker}
          exchange={position.exchange}
          quantity={position.quantity}
          onSuccess={() => { setShowRollDialog(false); setSelectedRollStrategy(null); fetchPosition(); }}
          onCancel={() => { setShowRollDialog(false); setSelectedRollStrategy(null); }}
          initialRollType={selectedRollStrategy?.rollType}
          initialValues={
            selectedRollStrategy &&
            selectedRollStrategy.strike !== undefined &&
            selectedRollStrategy.expiry !== undefined &&
            selectedRollStrategy.premium !== undefined
              ? {
                  newStrikePrice: selectedRollStrategy.strike,
                  newExpiryDate: selectedRollStrategy.expiry,
                  newPremium: selectedRollStrategy.premium,
                  buybackCost: position.lastOptionPrice ?? 0,
                }
              : undefined
          }
        />
      )}
      {showResaleDialog && (
        <ResaleDialog
          positionId={position.positionId}
          ticker={position.ticker}
          quantity={position.quantity}
          exchange={position.exchange}
          onSuccess={() => { setShowResaleDialog(false); fetchPosition(); fetchOptionHistory(); }}
          onCancel={() => setShowResaleDialog(false)}
        />
      )}
      {showManageCloseDialog && (
        <ManageCloseDialog
          positionId={position.positionId}
          ticker={position.ticker}
          exchange={position.exchange}
          entryPrice={position.purchasePrice}
          quantity={position.quantity}
          totalCommissions={position.totalCommissions}
          currentOption={position.currentOption}
          brokerProfiles={brokerProfiles}
          onCloseSuccess={() => { setShowManageCloseDialog(false); fetchPosition(); }}
          onOptionOutcomeSuccess={() => { setShowManageCloseDialog(false); fetchPosition(); fetchOptionHistory(); }}
          onCancel={() => setShowManageCloseDialog(false)}
        />
      )}

      {/* Trade details section */}
      <section aria-labelledby="trade-details-heading" style={{ marginBottom: '1.5rem' }}>
        <h3 id="trade-details-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
          Trade Details
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
          <DetailItem label="Trade Date" value={new Date(position.tradeDate).toLocaleDateString()} />
          <DetailItem label="Purchase Price" value={`$${position.purchasePrice.toFixed(2)}`} />
          <DetailItem label="Breakeven" value={breakeven !== undefined ? `$${breakeven.toFixed(2)}` : 'N/A'} info={FIELD_TIPS.breakeven} />
          <DetailItem label="Quantity" value={position.quantity.toLocaleString()} />
          {/* Entry Commission = all commissions incurred at entry, i.e. total less
              any exit-leg commissions. This captures both the stock buy AND the
              protective-put (or covered-call) entry commission, whereas
              stockEntryCommission alone is only the stock leg. */}
          <DetailItem label="Entry Commission" value={`$${entryCommission.toFixed(2)}`} info={FIELD_TIPS.entryCommission} />
          {position.currentOption && (
            <>
              <DetailItem label="Strike Price" value={`$${position.currentOption.strikePrice.toFixed(2)}`} />
              <DetailItem label="Premium" value={`$${position.currentOption.premium.toFixed(2)}/share`} />
              <DetailItem label="Expiry Date" value={new Date(position.currentOption.expiryDate).toLocaleDateString()} />
              <DetailItem label="Option Status" value={position.currentOption.status} />
            </>
          )}
          {position.exitDate && (
            <>
              <DetailItem label="Exit Date" value={new Date(position.exitDate).toLocaleDateString()} />
              <DetailItem label="Exit Stock Price" value={position.exitStockPrice !== undefined ? `$${position.exitStockPrice.toFixed(2)}` : 'N/A'} />
              {/* Only show buyback cost when a short option was actually bought back. */}
              {position.currentOption && position.exitOptionBuybackCost !== undefined && position.exitOptionBuybackCost > 0 && (
                <DetailItem label="Exit Buyback Cost" value={`$${position.exitOptionBuybackCost.toFixed(2)}`} />
              )}
              <DetailItem label="Exit Commissions" value={position.exitCommissions !== undefined ? `$${position.exitCommissions.toFixed(2)}` : 'N/A'} />
            </>
          )}
        </div>
      </section>

      {/* Long protective-put panel — distinct from the short-call currentOption
          panel above. Shown whenever the position carries a protective put.
          Requirements 8.1, 8.2, 8.3: long direction, premium labelled "paid",
          own strike/expiry/contracts/status, coexisting with any covered call. */}
      {position.protectivePut && (
        <section aria-labelledby="protective-put-heading" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
            <h3 id="protective-put-heading" style={{ margin: 0 }}>
              Protective Put
            </h3>
            <span
              style={{
                display: 'inline-block',
                padding: '0.125rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: 'var(--color-brand-fill)',
                color: 'var(--color-link)',
              }}
            >
              Long
            </span>
            <span
              style={{
                display: 'inline-block',
                padding: '0.125rem 0.5rem',
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: position.protectivePut.status === 'ACTIVE' ? 'var(--color-chip-success-bg)' : 'var(--color-surface-2)',
                color: position.protectivePut.status === 'ACTIVE' ? 'var(--color-chip-success-text)' : 'var(--color-muted)',
              }}
            >
              {position.protectivePut.status}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
            <DetailItem label="Strike Price" value={`$${position.protectivePut.strikePrice.toFixed(2)}`} />
            <DetailItem label="Premium (paid)" value={`$${position.protectivePut.premium.toFixed(2)}/share`} />
            <DetailItem label="Expiry Date" value={new Date(position.protectivePut.expiryDate).toLocaleDateString()} />
            <DetailItem label="Contracts" value={position.protectivePut.contracts.toLocaleString()} />
            <DetailItem label="Status" value={position.protectivePut.status} />
            {position.protectivePut.outcome && (
              <DetailItem label="Outcome" value={position.protectivePut.outcome} />
            )}
          </div>

          {/* Lifecycle actions — available while the put is ACTIVE and the
              position is OPEN. Opens the PutOutcomeDialog to record the outcome. */}
          {position.status === 'OPEN' && position.protectivePut.status === 'ACTIVE' && (
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={() => setShowPutOutcomeDialog(true)}
                style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-accent)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', minHeight: '44px' }}
              >
                Record Put Outcome (Expire / Sell to close / Exercise)
              </button>
            </div>
          )}
        </section>
      )}

      {/* Put outcome dialog */}
      {showPutOutcomeDialog && position.protectivePut && (
        <PutOutcomeDialog
          positionId={position.positionId}
          exchange={position.exchange}
          quantity={position.quantity}
          protectivePut={position.protectivePut}
          brokerProfiles={brokerProfiles}
          onSuccess={() => {
            setShowPutOutcomeDialog(false);
            fetchPosition();
            fetchPutHistory();
          }}
          onCancel={() => setShowPutOutcomeDialog(false)}
        />
      )}

      {/* Add protective put dialog — attaches a long put to this holding */}
      {showAddPutDialog && (
        <AddProtectivePutDialog
          positionId={position.positionId}
          ticker={position.ticker}
          quantity={position.quantity}
          exchange={position.exchange}
          brokerProfiles={brokerProfiles}
          onSuccess={() => {
            setShowAddPutDialog(false);
            fetchPosition();
            fetchPutHistory();
          }}
          onCancel={() => setShowAddPutDialog(false)}
        />
      )}

      {/* P&L breakdown section */}
      <section aria-labelledby="pnl-heading" style={{ marginBottom: '1.5rem' }}>
        <h3 id="pnl-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
          Profit & Loss {pnlBreakdown ? (pnlBreakdown.isRealised ? '(Realised)' : '(Unrealised)') : ''}
        </h3>
        {pnlBreakdown ? (
          pnlBreakdown.isRealised ? (
            /* Realised: the buyback cost (netRollCostCredit) is an actual
               incurred cost, so it stays in the Net P&L total unchanged. */
            <div style={{ display: 'grid', gap: '0.5rem', maxWidth: '400px' }}>
              <PnLLineItem label="Premium Received" value={pnlBreakdown.premiumReceived} info={FIELD_TIPS.premiumReceived} />
              <PnLLineItem label="Stock Gain/Loss" value={pnlBreakdown.stockGainLoss} info={FIELD_TIPS.stockGainLoss} />
              <PnLLineItem label="Roll Costs / Option Liability" value={pnlBreakdown.netRollCostCredit} info={FIELD_TIPS.rollCostsOptionLiability} />
              {pnlBreakdown.protectivePutCost !== undefined && pnlBreakdown.protectivePutCost !== 0 && (
                <PnLLineItem label="Protective Put Cost (paid)" value={-pnlBreakdown.protectivePutCost} isDeduction info={FIELD_TIPS.protectivePutCost} />
              )}
              <PnLLineItem label="Dividend Income" value={pnlBreakdown.dividendIncome ?? 0} info={FIELD_TIPS.dividendIncome} />
              <PnLLineItem label="Total Commissions" value={-pnlBreakdown.totalCommissions} isDeduction info={FIELD_TIPS.totalCommissions} />
              <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                <PnLLineItem label="Net P&L" value={pnlBreakdown.netProfitLoss} isBold info={FIELD_TIPS.netPnL} />
              </div>
            </div>
          ) : (
            /* Unrealised: the active-call buyback liability is only incurred if
               the short call is closed/rolled, so it is EXCLUDED from the
               headline "income to date" figure (which matches the Dashboard's
               Net P&L). The liability and the full mark-to-market value are
               shown separately below for rolling decisions. Display-only. */
            <div style={{ display: 'grid', gap: '0.5rem', maxWidth: '400px' }}>
              <PnLLineItem label="Premium Received" value={pnlBreakdown.premiumReceived} info={FIELD_TIPS.premiumReceived} />
              {/* Display-only: when a written call is active and the stock has
                  risen above the strike, show an informational capped
                  ("to strike") stock-gain line with a note explaining the cap,
                  followed by the full "Total stock gain" line. The capped line
                  is NOT summed into the total — the full stock gain
                  (pnlBreakdown.stockGainLoss) is what feeds the headline totals.
                  Otherwise the single line renders unchanged. */}
              {showStockGainSplit && stockGainSplit ? (
                <>
                  <PnLLineItem label="Stock gain (to strike)" value={stockGainSplit.toStrike} info={FIELD_TIPS.stockGainToStrike} />
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-muted)', lineHeight: 1.35 }}>
                    Capped at the ${strikePrice!.toFixed(2)} strike by your written call — gains above this are offset by the cost to close it.
                  </p>
                  <PnLLineItem label="Total stock gain" value={pnlBreakdown.stockGainLoss} info={FIELD_TIPS.totalStockGain} />
                </>
              ) : (
                <PnLLineItem label="Stock Gain/Loss" value={pnlBreakdown.stockGainLoss} info={FIELD_TIPS.stockGainLoss} />
              )}
              {pnlBreakdown.protectivePutCost !== undefined && pnlBreakdown.protectivePutCost !== 0 && (
                <PnLLineItem label="Protective Put Cost (paid)" value={-pnlBreakdown.protectivePutCost} isDeduction info={FIELD_TIPS.protectivePutCost} />
              )}
              {/* Dividend income — a credit (positive/green). Rendered ALWAYS,
                  including the zero case, so OPEN/CLOSED views stay consistent
                  (Req 4.2, 4.5). */}
              <PnLLineItem label="Dividend Income" value={pnlBreakdown.dividendIncome ?? 0} info={FIELD_TIPS.dividendIncome} />
              <PnLLineItem label="Total Commissions" value={-pnlBreakdown.totalCommissions} isDeduction info={FIELD_TIPS.totalCommissions} />
              <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                <PnLLineItem label="Net P&L (income to date)" value={incomeToDate} isBold info={FIELD_TIPS.netPnLIncomeToDate} />
              </div>
              {/* Secondary mark-to-market grouping — shown whenever a written
                  call is active. When a current option price is available the
                  cost to close and the full mark-to-market value are shown;
                  without a price (e.g. a just-sold call with no market data yet)
                  the cost is N/A and MTM equals income to date. */}
              {hasActiveCall && (
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.5rem', marginTop: '0.5rem', display: 'grid', gap: '0.5rem' }}>
                  {hasOptionPrice ? (
                    <>
                      <PnLLineItem label="Cost to close call" value={optionLiability} info={FIELD_TIPS.costToCloseCall} />
                      <PnLLineItem label="Mark-to-market value" value={markToMarket} isBold info={FIELD_TIPS.markToMarketValue} />
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-muted)', lineHeight: 1.35 }}>
                        Mark-to-market assumes buying back the open call at its current price; that cost is only incurred if you close or roll it.
                      </p>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                          <span>Cost to close call</span>
                          <FieldInfo label="Cost to close call" tip={FIELD_TIPS.costToCloseCall} />
                        </span>
                        <span style={{ color: 'var(--color-muted)', fontFamily: 'monospace' }}>N/A</span>
                      </div>
                      <PnLLineItem label="Mark-to-market value" value={markToMarket} isBold info={FIELD_TIPS.markToMarketValue} />
                      <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--color-muted)', lineHeight: 1.35 }}>
                        A current option price is needed to value the open call. Update prices to see the cost to close and mark-to-market value.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        ) : (
          <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
            P&L is not available until market prices are retrieved or manually entered.
          </p>
        )}
      </section>

      {/* Tabbed sections — Market Data (default), IV Environment, Rolling
          Strategy Guidance, Dividends, Trade Reports. Inserted where the Market
          Data section began; the five section bodies now live in the panel
          render functions above. Everything else on the page stays untabbed. */}
      <Tabs
        tabs={tabs}
        selectedId={effectiveTabId}
        onSelect={setSelectedTabId}
        ariaLabel="Position detail sections"
      />

      {/* IV display when no market data section shows it */}
      {!impliedVolatility && position.lastImpliedVolatility === undefined && position.currentOption?.status === 'ACTIVE' && (
        <section style={{ marginBottom: '1.5rem' }}>
          <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>
            Implied volatility is currently unavailable.
          </p>
        </section>
      )}

      {/* Cumulative premium income section */}
      {cumulativePremium !== undefined && optionHistory.length > 0 && (
        <section aria-labelledby="premium-history-heading" style={{ marginBottom: '1.5rem' }}>
          <h3 id="premium-history-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
            Option History & Cumulative Premium
          </h3>
          <p style={{ marginBottom: '0.75rem' }}>
            <strong>Cumulative Premium Income:</strong>{' '}
            <span style={{ color: cumulativePremium >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)', fontWeight: 600 }}>
              ${cumulativePremium.toFixed(2)}
            </span>
            {' '}({optionHistory.length} option{optionHistory.length !== 1 ? ' sales' : ' sale'} × {quantity.toLocaleString()} shares)
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '700px' }} aria-label="Option history">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>#</th>
                  <th style={{ padding: '0.5rem' }}>Strike</th>
                  <th style={{ padding: '0.5rem' }}>Premium/share</th>
                  <th style={{ padding: '0.5rem' }}>Total Income</th>
                  <th style={{ padding: '0.5rem' }}>Expiry</th>
                  <th style={{ padding: '0.5rem' }}>Outcome</th>
                  <th style={{ padding: '0.5rem' }}>Buyback/share</th>
                  <th style={{ padding: '0.5rem' }}>Total Buyback</th>
                </tr>
              </thead>
              <tbody>
                {optionHistory.map((entry) => (
                  <tr key={entry.sequence} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem' }}>{entry.sequence}</td>
                    <td style={{ padding: '0.5rem' }}>${entry.strikePrice.toFixed(2)}</td>
                    <td style={{ padding: '0.5rem' }}>${entry.premium.toFixed(2)}</td>
                    <td style={{ padding: '0.5rem' }}>${(entry.premium * quantity).toFixed(2)}</td>
                    <td style={{ padding: '0.5rem' }}>{new Date(entry.expiryDate).toLocaleDateString()}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.outcome}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.buybackCost !== undefined ? `$${entry.buybackCost.toFixed(2)}` : '-'}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.buybackCost !== undefined ? `$${(entry.buybackCost * quantity).toFixed(2)}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Roll history — each roll's economics, including the net credit/debit
          (positive = credit received, negative = debit paid). Shown whenever a
          roll has been recorded on the position. */}
      {rollHistory.length > 0 && (
        <section aria-labelledby="roll-history-heading" style={{ marginBottom: '1.5rem' }}>
          <h3 id="roll-history-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
            Roll History
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '700px' }} aria-label="Roll history">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>#</th>
                  <th style={{ padding: '0.5rem' }}>Type</th>
                  <th style={{ padding: '0.5rem' }}>Strike</th>
                  <th style={{ padding: '0.5rem' }}>Expiry</th>
                  <th style={{ padding: '0.5rem' }}>Buyback/share</th>
                  <th style={{ padding: '0.5rem' }}>New Premium/share</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Net Credit/Debit</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Strike Gain (if assigned)</th>
                  <th style={{ padding: '0.5rem' }}>Rolled</th>
                </tr>
              </thead>
              <tbody>
                {rollHistory.map((entry) => {
                  const rollTypeLabels: Record<string, string> = {
                    UP: 'Roll Up',
                    DOWN: 'Roll Down',
                    OUT: 'Roll Out',
                    UP_AND_OUT: 'Roll Up & Out',
                  };
                  return (
                    <tr key={entry.sequence} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.5rem' }}>{entry.sequence}</td>
                      <td style={{ padding: '0.5rem' }}>{rollTypeLabels[entry.rollType] ?? entry.rollType}</td>
                      <td style={{ padding: '0.5rem' }}>${entry.previousStrike.toFixed(2)} &rarr; ${entry.newStrike.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem' }}>{new Date(entry.previousExpiry).toLocaleDateString()} &rarr; {new Date(entry.newExpiry).toLocaleDateString()}</td>
                      <td style={{ padding: '0.5rem' }}>${entry.buybackCost.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem' }}>${entry.newPremium.toFixed(2)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontFamily: 'monospace', color: entry.netCreditDebit >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)' }}>
                        {entry.netCreditDebit >= 0 ? '+' : ''}${entry.netCreditDebit.toFixed(2)}
                      </td>
                      {(() => {
                        const strikeGain = (entry.newStrike - entry.previousStrike) * quantity;
                        return (
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontFamily: 'monospace', color: strikeGain > 0 ? 'var(--color-pnl-positive)' : strikeGain < 0 ? 'var(--color-pnl-negative)' : 'var(--color-muted)' }}>
                            {strikeGain === 0 ? '—' : `${strikeGain >= 0 ? '+' : ''}${strikeGain.toFixed(2)}`}
                          </td>
                        );
                      })()}
                      <td style={{ padding: '0.5rem' }}>{new Date(entry.rollDate).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Protective put history — recorded outcomes for completed puts
          (Requirements 8.4, 8.5). Displayed whenever any put outcome exists,
          regardless of whether an active put is currently held. */}
      {putHistory.length > 0 && (
        <section aria-labelledby="put-history-heading" style={{ marginBottom: '1.5rem' }}>
          <h3 id="put-history-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
            Protective Put History
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', minWidth: '700px' }} aria-label="Protective put history">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>#</th>
                  <th style={{ padding: '0.5rem' }}>Strike</th>
                  <th style={{ padding: '0.5rem' }}>Premium/share (paid)</th>
                  <th style={{ padding: '0.5rem' }}>Contracts</th>
                  <th style={{ padding: '0.5rem' }}>Expiry</th>
                  <th style={{ padding: '0.5rem' }}>Outcome</th>
                  <th style={{ padding: '0.5rem' }}>Sale Proceeds/share</th>
                  <th style={{ padding: '0.5rem' }}>Closed</th>
                </tr>
              </thead>
              <tbody>
                {putHistory.map((entry) => (
                  <tr key={entry.sequence} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.5rem' }}>{entry.sequence}</td>
                    <td style={{ padding: '0.5rem' }}>${entry.strikePrice.toFixed(2)}</td>
                    <td style={{ padding: '0.5rem' }}>${entry.premium.toFixed(2)}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.contracts.toLocaleString()}</td>
                    <td style={{ padding: '0.5rem' }}>{new Date(entry.expiryDate).toLocaleDateString()}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.outcome}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.saleProceedsPerShare !== undefined ? `$${entry.saleProceedsPerShare.toFixed(2)}` : '-'}</td>
                    <td style={{ padding: '0.5rem' }}>{entry.closedDate ? new Date(entry.closedDate).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Add dividend dialog — available on OPEN and CLOSED positions */}
      {showAddDividendDialog && (
        <AddDividendDialog
          positionId={position.positionId}
          ticker={position.ticker}
          quantity={position.quantity}
          onSuccess={() => {
            setShowAddDividendDialog(false);
            fetchPosition();
            fetchDividends();
          }}
          onCancel={() => setShowAddDividendDialog(false)}
        />
      )}

      {/* Edit dividend dialog — pre-populated from the selected record */}
      {editingDividend && (
        <EditDividendDialog
          positionId={position.positionId}
          ticker={position.ticker}
          quantity={position.quantity}
          dividend={editingDividend}
          onSuccess={() => {
            setEditingDividend(null);
            fetchPosition();
            fetchDividends();
          }}
          onCancel={() => setEditingDividend(null)}
        />
      )}

    </div>
  );
}

/**
 * Trade Reports section for the Position Detail page.
 *
 * Renders identically for OPEN and CLOSED positions — nothing here branches on
 * position status (Req 9.3, Property 8). Lists uploaded IB trade-report PDFs and
 * provides upload, view, and delete controls. PDF/size validation happens on the
 * client before any network call (Req 2.3, 2.4, 10.4).
 *
 * Validates: Requirements 2.3, 2.4, 6.4, 9.3, 10.1, 10.2, 10.3, 10.4, 10.5, 10.6, 10.7
 */
const MAX_REPORT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB, mirrors server MAX_SIZE_BYTES

export function TradeReports({ positionId }: { positionId: string }) {
  const [reports, setReports] = useState<TradeReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  // isBusy disables all controls during any in-flight op (upload/view/delete).
  const [isBusy, setIsBusy] = useState(false);
  // isUploading is scoped to the upload action so the Upload button label only
  // reflects a real upload (not a View/Delete that also sets isBusy).
  const [isUploading, setIsUploading] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  // Ref to the native file input so we can clear its displayed filename after a
  // successful upload — setting the `file` state to null is not enough because
  // an uncontrolled <input type="file"> keeps its own DOM value.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState<TradeReportLabel>(TradeReportLabel.ENTRY);
  const [note, setNote] = useState('');
  // The upload form is collapsed by default to conserve screen space; the
  // uploaded-report list above stays visible.
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await listReports(positionId);
      setReports(data.reports);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setLoadError(err.message);
      } else {
        setLoadError('Failed to load trade reports.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [positionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleUpload = async () => {
    setStatus(null);

    // Client-side validation BEFORE any network call (Req 2.3, 2.4, 10.4).
    if (!file) {
      setStatus('Please select a PDF file to upload.');
      return;
    }
    if (file.type !== 'application/pdf') {
      setStatus('Only PDF files are allowed.');
      return;
    }
    if (file.size > MAX_REPORT_SIZE_BYTES) {
      setStatus('File exceeds the 10 MB limit.');
      return;
    }

    setIsBusy(true);
    setIsUploading(true);
    try {
      const meta = {
        label,
        note: note.trim() ? note.trim() : undefined,
        filename: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      };
      const presign = await requestReportUpload(positionId, meta);
      await uploadReportToS3(presign, file);
      await confirmReportUpload(positionId, presign.reportId, meta);
      await refresh();
      // Reset the form. Clear the native input value too so the chosen-file
      // label is cleared (state alone does not reset an uncontrolled file input).
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setNote('');
      setLabel(TradeReportLabel.ENTRY);
      setStatus(`Uploaded ${meta.filename}.`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setStatus(err.message);
      } else if (err instanceof Error) {
        setStatus(err.message);
      } else {
        setStatus('Upload failed.');
      }
    } finally {
      setIsBusy(false);
      setIsUploading(false);
    }
  };

  const handleView = async (reportId: string, filename: string) => {
    setStatus(null);
    setIsBusy(true);
    try {
      const { viewUrl } = await getReportViewUrl(positionId, reportId);
      // Open via a programmatic anchor click rather than window.open — browsers
      // treat an anchor with target="_blank" more permissively (window.open
      // after an await is commonly popup-blocked). rel prevents the opened tab
      // from accessing window.opener.
      const anchor = document.createElement('a');
      anchor.href = viewUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setStatus(err.message);
      } else {
        setStatus(`Failed to open ${filename}.`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  const handleDelete = async (reportId: string, filename: string) => {
    setStatus(null);
    setIsBusy(true);
    try {
      await deleteReport(positionId, reportId);
      setReports((prev) => prev.filter((r) => r.reportId !== reportId));
      setStatus(`Deleted ${filename}.`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setStatus(err.message);
      } else {
        setStatus(`Failed to delete ${filename}.`);
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section aria-labelledby="trade-reports-heading" style={{ marginBottom: '1.5rem' }}>
      <h3 id="trade-reports-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
        Trade Reports
      </h3>

      {/* Status / error announcements (Req 10.7) */}
      <div aria-live="polite" style={{ minHeight: '0' }}>
        {status && (
          <div role="status" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', backgroundColor: 'var(--color-brand-fill)', color: 'var(--color-link)', borderRadius: '4px', fontSize: '0.85rem' }}>
            {status}
          </div>
        )}
        {loadError && (
          <div role="alert" style={{ padding: '0.5rem 0.75rem', marginBottom: '0.75rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px', fontSize: '0.85rem' }}>
            {loadError}
          </div>
        )}
      </div>

      {/* Report list (Req 10.1, 10.2) */}
      {isLoading ? (
        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>Loading trade reports...</p>
      ) : reports.length === 0 ? (
        <p style={{ color: 'var(--color-muted)', fontStyle: 'italic', fontSize: '0.875rem' }}>No trade reports uploaded yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
          {reports.map((r) => (
            <li
              key={r.reportId}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}
            >
              <div style={{ flex: '1 1 240px', minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.125rem 0.5rem', borderRadius: '12px', backgroundColor: 'var(--color-surface-2)', color: 'var(--color-muted)' }}>
                    {r.label}
                  </span>
                  <span style={{ fontWeight: 500 }}>{r.filename}</span>
                </div>
                {r.note && (
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--color-muted)' }}>{r.note}</p>
                )}
                <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                  Uploaded {new Date(r.uploadedAt).toLocaleDateString()}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => handleView(r.reportId, r.filename)}
                  disabled={isBusy}
                  aria-label={`View ${r.filename}`}
                  style={{ padding: '0.375rem 0.75rem', backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-brand)', borderRadius: '4px', cursor: isBusy ? 'not-allowed' : 'pointer', fontSize: '0.8rem', minHeight: '44px' }}
                >
                  View
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(r.reportId, r.filename)}
                  disabled={isBusy}
                  aria-label={`Delete ${r.filename}`}
                  style={{ padding: '0.375rem 0.75rem', backgroundColor: 'transparent', color: 'var(--color-danger)', border: '1px solid var(--color-danger)', borderRadius: '4px', cursor: isBusy ? 'not-allowed' : 'pointer', fontSize: '0.8rem', minHeight: '44px' }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Upload control (Req 10.3, 10.5) — collapsible to conserve space */}
      <div style={{ padding: '1rem', backgroundColor: 'var(--color-surface-2)', borderRadius: '4px', border: '1px solid var(--color-border)', maxWidth: '600px' }}>
        <button
          type="button"
          onClick={() => setIsUploadOpen((open) => !open)}
          aria-expanded={isUploadOpen}
          aria-controls="trade-report-upload-form"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            width: '100%',
            background: 'none',
            border: 'none',
            padding: 0,
            margin: 0,
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--color-text)',
            minHeight: 'auto',
          }}
        >
          <span aria-hidden="true" style={{ display: 'inline-block', transition: 'transform 0.15s', transform: isUploadOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
            &#9656;
          </span>
          Upload a Trade Report
        </button>
        {isUploadOpen && (
        <div id="trade-report-upload-form" style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
          <div>
            <label htmlFor="trade-report-file" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
              PDF File
            </label>
            <input
              id="trade-report-file"
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              disabled={isBusy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              style={{ width: '100%', fontSize: '0.875rem' }}
            />
          </div>
          <div>
            <label htmlFor="trade-report-label" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
              Report Type
            </label>
            <select
              id="trade-report-label"
              value={label}
              disabled={isBusy}
              onChange={(e) => setLabel(e.target.value as TradeReportLabel)}
              style={{ width: '100%', maxWidth: '260px', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', boxSizing: 'border-box' }}
            >
              <option value={TradeReportLabel.ENTRY}>Entry</option>
              <option value={TradeReportLabel.ADDITIONAL_OPTION}>Additional Option</option>
              <option value={TradeReportLabel.EXIT}>Exit</option>
              <option value={TradeReportLabel.OTHER}>Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="trade-report-note" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500, fontSize: '0.875rem' }}>
              Note (optional)
            </label>
            <input
              id="trade-report-note"
              type="text"
              value={note}
              maxLength={1000}
              disabled={isBusy}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Initial entry statement"
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--color-input-border)', borderRadius: '4px', boxSizing: 'border-box' }}
            />
          </div>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isBusy}
            aria-busy={isUploading}
            style={{ padding: '0.5rem 1rem', backgroundColor: isBusy ? 'var(--color-muted)' : 'var(--color-success)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: isBusy ? 'not-allowed' : 'pointer', minHeight: '44px', width: 'fit-content', opacity: isBusy ? 0.7 : 1 }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
        )}
      </div>
    </section>
  );
}

/**
 * Tip copy for the computed/derived fields on the Position Detail screen. Centralised as a single
 * source of truth so call sites stay short (`info={FIELD_TIPS.breakeven}`) and shared fields that
 * appear in more than one branch cannot drift. Strings are verbatim from requirements.md Req 3/4.
 */
export const FIELD_TIPS = {
  breakeven: 'The stock price you need to stay above to avoid a loss once premiums are counted. Calculated as your purchase price minus the net option premium collected per share.',
  entryCommission: 'All commissions you paid to open this position, including the stock purchase and any option or protective-put entry leg. Derived as your total commissions minus the exit-leg commissions.',
  premiumReceived: 'The total option premium income you have collected on this position, net of any buybacks, multiplied by your share quantity. Counted as income in the total.',
  stockGainLoss: 'The change in the stock price since purchase multiplied by your share quantity. A positive figure is an unrealised or realised gain; a negative figure is a loss.',
  stockGainToStrike: "An informational figure showing your stock gain capped at the written call's strike, because gains above the strike are offset by the cost to close that call. This line is not added to the total; the full 'Total stock gain' is what feeds the P&L.",
  totalStockGain: "The full change in the stock price since purchase multiplied by your share quantity. This is the stock figure included in the P&L total, as opposed to the informational capped 'to strike' figure above it.",
  rollCostsOptionLiability: 'The cost tied to the current written call. For an open position this is the mark-to-market cost to buy it back at the current price; for a closed position it is the actual buyback cost paid.',
  protectivePutCost: 'The premium you paid for the protective put, multiplied by your quantity. It is subtracted from the total as a cost.',
  dividendIncome: 'The total of all dividends recorded against this position. It is added to the total as a credit.',
  totalCommissions: 'Every commission paid on this position across both the entry and exit legs. It is subtracted from the total as a cost.',
  netPnL: 'The headline profit or loss for this position: premium income plus stock movement and dividends, minus commissions, the protective-put cost, and the written-call buyback cost.',
  netPnLIncomeToDate: 'Your profit so far on an open position, excluding the cost to buy back the still-open written call. This excludes the open call\'s buy-back liability and matches the Net P&L shown on the Dashboard.',
  costToCloseCall: 'The current cost to buy back your open written call at its market price. You only incur this if you close or roll the call, so it is shown separately from income to date. It reads N/A when no current option price is available.',
  markToMarketValue: 'The full current value of this position if you closed it now at current prices: your income to date minus the cost to close the open written call.',
} as const;

/**
 * Reusable accessible "i" tooltip affordance for a computed field. Generalises the IvInfoButton
 * pattern, exposing the two things that vary per field — the field name (for the aria-label) and
 * the tip copy — as props. Owns its own open/closed state and renders a full-screen click-catcher
 * plus a role="tooltip" popup while open.
 */
export function FieldInfo({ label, tip }: { label: string; tip: string }) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setShowInfo(!showInfo)}
        aria-label={`What does ${label} mean?`}
        aria-expanded={showInfo}
        style={{
          background: 'none',
          border: '1px solid var(--color-muted)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          minHeight: '20px',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--color-muted)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        i
      </button>
      {showInfo && (
        <>
          <div
            onClick={() => setShowInfo(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          />
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: '28px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--color-header-bg)',
              color: 'var(--color-header-text)',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '0.8rem',
              lineHeight: '1.5',
              width: '320px',
              zIndex: 1000,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            <p style={{ marginBottom: '0.5rem' }}>{tip}</p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              Tap anywhere to close
            </p>
          </div>
        </>
      )}
    </span>
  );
}

/** Info button with popup explaining IV percentile ranges for covered call selling. */
function IvInfoButton() {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setShowInfo(!showInfo)}
        aria-label="What does IV percentile mean?"
        style={{
          background: 'none',
          border: '1px solid var(--color-muted)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          minHeight: '20px',
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--color-muted)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        i
      </button>
      {showInfo && (
        <>
          <div
            onClick={() => setShowInfo(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 999 }}
          />
          <div
            role="tooltip"
            style={{
              position: 'absolute',
              top: '28px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'var(--color-header-bg)',
              color: 'var(--color-header-text)',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '0.8rem',
              lineHeight: '1.5',
              width: '320px',
              zIndex: 1000,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
          >
            <p style={{ fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.85rem' }}>IV Percentile Guide</p>
            <p style={{ marginBottom: '0.5rem' }}>
              Shows where current implied volatility sits relative to its 52-week range. Higher = richer option premiums.
            </p>
            <div style={{ marginBottom: '0.4rem' }}>
              <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>70-100%</span>
              <span> — Premiums are rich. Ideal time to sell or roll covered calls. Consider further OTM strikes as even they pay well.</span>
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>40-70%</span>
              <span> — Fair value. Use your standard strike selection and expiry.</span>
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>0-40%</span>
              <span> — Premiums are cheap. Consider waiting for IV to rise, or sell closer to the money to get a worthwhile premium.</span>
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              Tap anywhere to close
            </p>
          </div>
        </>
      )}
    </span>
  );
}

/**
 * Maps a recommendation strategy label to a concrete RollType, or null when the
 * strategy is not a roll (e.g. 'Let expire & resell', 'Hold current position').
 * Order matters: check the more specific 'up and out' before 'up'/'out'.
 */
function strategyToRollType(strategy: string): RollType | null {
  const s = strategy.toLowerCase();
  if (s.includes('roll up') && s.includes('out')) return RollType.UP_AND_OUT;
  if (s.includes('roll out')) return RollType.OUT;
  if (s.includes('roll up')) return RollType.UP;
  if (s.includes('roll down')) return RollType.DOWN;
  return null;
}

/** Rolling Strategy Guidance — analyses the position and suggests strategies. */
function RollingGuidance({
  stockPrice,
  strikePrice,
  premium,
  expiryDate,
  optionPrice,
  entryPrice,
  optionQuotes = [],
  quantity = 1,
  commissionPerTrade = 0,
  onSelectRoll,
}: {
  stockPrice: number;
  strikePrice: number;
  premium: number;
  expiryDate: string;
  optionPrice?: number;
  entryPrice: number;
  optionQuotes?: Array<{ expiry: string; strike: number; bid: number; ask: number }>;
  quantity?: number;
  commissionPerTrade?: number;
  onSelectRoll?: (params: { strategy: string; rollType?: RollType; strike?: number; expiry?: string; premium?: number }) => void;
}) {
  const today = new Date();
  const expiry = new Date(expiryDate);
  const daysToExpiry = Math.max(0, Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

  const isITM = stockPrice > strikePrice;
  const isATM = Math.abs(stockPrice - strikePrice) / strikePrice < 0.02; // within 2%
  const isDeepITM = stockPrice > strikePrice * 1.05; // more than 5% ITM
  const percentFromStrike = ((stockPrice - strikePrice) / strikePrice * 100).toFixed(1);

  // Time decay analysis
  const isNearExpiry = daysToExpiry <= 7;
  const isMidTerm = daysToExpiry > 7 && daysToExpiry <= 21;
  const isLongDated = daysToExpiry > 21;

  // Option value analysis (if we have current option price)
  const premiumCaptured = optionPrice !== undefined
    ? ((premium - optionPrice) / premium * 100).toFixed(0)
    : null;

  // Generate recommendations
  const recommendations: Array<{ strategy: string; rationale: string; priority: 'high' | 'medium' | 'low' }> = [];

  // Near expiry + OTM = let expire, consider resale
  if (isNearExpiry && !isITM) {
    recommendations.push({
      strategy: 'Let expire & resell',
      rationale: `Option is OTM with only ${daysToExpiry} days remaining. Time decay is maximally in your favour. Let it expire worthless and sell a new option for next month.`,
      priority: 'high',
    });
  }

  // Near expiry + ITM = consider rolling out or up-and-out
  if (isNearExpiry && isITM) {
    recommendations.push({
      strategy: 'Roll Out',
      rationale: `Stock is ${percentFromStrike}% above strike with ${daysToExpiry} days left. Roll to next month at the same strike to collect additional premium and avoid assignment. Check the available premium at the same strike for next month's expiry to assess if the credit received justifies the roll.`,
      priority: 'high',
    });
    recommendations.push({
      strategy: 'Roll Up and Out',
      rationale: `Roll to a higher strike at a later expiry. This gives more upside room (reducing assignment risk) while extending the trade. The higher strike typically means less premium, but the longer expiry adds time value. Compare the net credit/debit against simply rolling out at the same strike.`,
      priority: 'high',
    });
  }

  // Deep ITM at any point = consider rolling up
  if (isDeepITM && !isNearExpiry) {
    recommendations.push({
      strategy: 'Roll Up',
      rationale: `Stock is ${percentFromStrike}% above strike. Consider rolling up to a higher strike (same expiry) to capture more stock upside. This typically costs a net debit.`,
      priority: 'medium',
    });
    recommendations.push({
      strategy: 'Roll Up and Out',
      rationale: `Roll to higher strike and later expiry. The later expiry helps offset the cost of moving to a higher strike, potentially for a net credit.`,
      priority: 'medium',
    });
  }

  // Stock dropped significantly = consider rolling down
  if (!isITM && stockPrice < entryPrice * 0.95 && !isNearExpiry) {
    recommendations.push({
      strategy: 'Roll Down',
      rationale: `Stock has fallen below entry. Roll down to a lower strike (same expiry) to collect more premium and reduce your cost basis. This provides more downside protection.`,
      priority: 'medium',
    });
  }

  // Mid-term with significant premium captured
  if (isMidTerm && premiumCaptured !== null && parseInt(premiumCaptured) >= 80 && !isITM) {
    recommendations.push({
      strategy: 'Buy back early & resell',
      rationale: `${premiumCaptured}% of premium has been captured with ${daysToExpiry} days remaining. Consider buying back the option cheaply and selling a new one for next month to reset premium income.`,
      priority: 'medium',
    });
  }

  // Long-dated + OTM = hold, theta working for you
  if (isLongDated && !isITM) {
    recommendations.push({
      strategy: 'Hold current position',
      rationale: `Option is OTM with ${daysToExpiry} days remaining. Time decay is working in your favour. No action needed unless the stock moves significantly.`,
      priority: 'low',
    });
  }

  // No specific recommendation
  if (recommendations.length === 0) {
    recommendations.push({
      strategy: 'Hold current position',
      rationale: `Position is within expected parameters. Monitor for significant price movement or as expiry approaches.`,
      priority: 'low',
    });
  }

  // Calculate rolling economics from option quotes
  const currentBuybackCost = optionPrice ?? 0;
  const totalRollCommission = commissionPerTrade * 2; // buyback + new sale
  const rollEconomics: Array<{ strategy: string; netCreditDebit: number; description: string; expiry: string; strike: number; newBid: number }> = [];

  if (optionQuotes.length > 0) {
    for (const quote of optionQuotes) {
      // Roll Out: same strike, later expiry — sell at quote bid
      if (quote.strike === strikePrice) {
        const netPerShare = quote.bid - currentBuybackCost;
        const netTotal = (netPerShare * quantity) - totalRollCommission;
        rollEconomics.push({
          strategy: `Roll Out to ${new Date(quote.expiry).toLocaleDateString()}`,
          expiry: quote.expiry,
          strike: quote.strike,
          newBid: quote.bid,
          netCreditDebit: netTotal,
          description: `Buy back @ $${currentBuybackCost.toFixed(3)}, sell ${new Date(quote.expiry).toLocaleDateString()} $${quote.strike.toFixed(2)} @ $${quote.bid.toFixed(3)}. Net/share: $${netPerShare >= 0 ? '+' : ''}${netPerShare.toFixed(3)}${totalRollCommission > 0 ? `, less $${totalRollCommission.toFixed(2)} commission (2 trades)` : ''}`,
        });
      }

      // Roll Up: higher strike, same expiry
      if (quote.strike > strikePrice && quote.expiry === expiryDate) {
        const netPerShare = quote.bid - currentBuybackCost;
        const netTotal = (netPerShare * quantity) - totalRollCommission;
        rollEconomics.push({
          strategy: `Roll Up to $${quote.strike.toFixed(2)} strike`,
          expiry: quote.expiry,
          strike: quote.strike,
          newBid: quote.bid,
          netCreditDebit: netTotal,
          description: `Buy back @ $${currentBuybackCost.toFixed(3)}, sell $${quote.strike.toFixed(2)} @ $${quote.bid.toFixed(3)}. Net/share: $${netPerShare >= 0 ? '+' : ''}${netPerShare.toFixed(3)}${totalRollCommission > 0 ? `, less $${totalRollCommission.toFixed(2)} commission (2 trades)` : ''}`,
        });
      }

      // Roll Up and Out: higher strike, later expiry
      if (quote.strike > strikePrice && quote.expiry !== expiryDate) {
        const netPerShare = quote.bid - currentBuybackCost;
        const netTotal = (netPerShare * quantity) - totalRollCommission;
        rollEconomics.push({
          strategy: `Roll Up & Out to $${quote.strike.toFixed(2)} @ ${new Date(quote.expiry).toLocaleDateString()}`,
          expiry: quote.expiry,
          strike: quote.strike,
          newBid: quote.bid,
          netCreditDebit: netTotal,
          description: `Buy back @ $${currentBuybackCost.toFixed(3)}, sell ${new Date(quote.expiry).toLocaleDateString()} $${quote.strike.toFixed(2)} @ $${quote.bid.toFixed(3)}. Net/share: $${netPerShare >= 0 ? '+' : ''}${netPerShare.toFixed(3)}${totalRollCommission > 0 ? `, less $${totalRollCommission.toFixed(2)} commission (2 trades)` : ''}`,
        });
      }

      // Roll Down: lower strike, same expiry
      if (quote.strike < strikePrice && quote.expiry === expiryDate) {
        const netPerShare = quote.bid - currentBuybackCost;
        const netTotal = (netPerShare * quantity) - totalRollCommission;
        rollEconomics.push({
          strategy: `Roll Down to $${quote.strike.toFixed(2)} strike`,
          expiry: quote.expiry,
          strike: quote.strike,
          newBid: quote.bid,
          netCreditDebit: netTotal,
          description: `Buy back @ $${currentBuybackCost.toFixed(3)}, sell $${quote.strike.toFixed(2)} @ $${quote.bid.toFixed(3)}. Net/share: $${netPerShare >= 0 ? '+' : ''}${netPerShare.toFixed(3)}${totalRollCommission > 0 ? `, less $${totalRollCommission.toFixed(2)} commission (2 trades)` : ''}`,
        });
      }
    }
  }

  const priorityColors = { high: 'var(--color-danger)', medium: 'var(--color-warning)', low: 'var(--color-success)' };
  const priorityLabels = { high: 'Action recommended', medium: 'Consider', low: 'No action needed' };

  return (
    <section aria-labelledby="rolling-guidance-heading" style={{ marginBottom: '1.5rem' }}>
      <h3 id="rolling-guidance-heading" style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}>
        Rolling Strategy Guidance
      </h3>

      {/* Position summary */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--color-muted)' }}>
        <span>Days to expiry: <strong style={{ color: daysToExpiry <= 7 ? 'var(--color-danger)' : 'var(--color-text)' }}>{daysToExpiry}</strong></span>
        <span>Stock vs Strike: <strong style={{ color: isITM ? 'var(--color-danger)' : 'var(--color-success)' }}>{isITM ? 'ITM' : isATM ? 'ATM' : 'OTM'} ({percentFromStrike}%)</strong></span>
        {premiumCaptured !== null && (
          <span>Premium captured: <strong>{premiumCaptured}%</strong></span>
        )}
      </div>

      {/* Recommendations */}
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {recommendations.map((rec, idx) => {
          // Find matching economics for this strategy
          const matchingEconomics = rollEconomics.filter(econ => {
            const strategy = rec.strategy.toLowerCase();
            const econStrategy = econ.strategy.toLowerCase();
            if (strategy.includes('roll out') && !strategy.includes('up and out') && econStrategy.includes('roll out')) return true;
            if (strategy.includes('roll up and out') && econStrategy.includes('roll up & out')) return true;
            if (strategy.includes('roll up') && !strategy.includes('out') && econStrategy.includes('roll up') && !econStrategy.includes('out')) return true;
            if (strategy.includes('roll down') && econStrategy.includes('roll down')) return true;
            return false;
          });

          return (
            <div
              key={idx}
              style={{
                padding: '0.75rem 1rem',
                borderLeft: `4px solid ${priorityColors[rec.priority]}`,
                backgroundColor: 'var(--color-surface-2)',
                borderRadius: '0 4px 4px 0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                <strong style={{ fontSize: '0.9rem' }}>{rec.strategy}</strong>
                <span style={{ fontSize: '0.7rem', padding: '0.125rem 0.5rem', borderRadius: '12px', backgroundColor: priorityColors[rec.priority], color: 'var(--color-on-brand)' }}>
                  {priorityLabels[rec.priority]}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-muted)' }}>{rec.rationale}</p>
              {(() => {
                const recRollType = strategyToRollType(rec.strategy);
                if (!recRollType) return null;
                return (
                  <button
                    type="button"
                    onClick={() => onSelectRoll?.({ strategy: rec.strategy, rollType: recRollType })}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.25rem 0.75rem',
                      fontSize: '0.75rem',
                      backgroundColor: 'var(--color-brand)',
                      color: 'var(--color-on-brand)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      minHeight: 'auto',
                    }}
                  >
                    Roll this option
                  </button>
                );
              })()}
              {matchingEconomics.length > 0 && (
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: 'var(--color-link)', fontWeight: 500 }}>
                    View economics ({matchingEconomics.length} option{matchingEconomics.length > 1 ? 's' : ''})
                  </summary>
                  <div style={{ marginTop: '0.5rem', paddingLeft: '0.5rem', borderLeft: '2px solid var(--color-border)' }}>
                    {matchingEconomics.sort((a, b) => a.expiry.localeCompare(b.expiry)).map((econ, eIdx) => (
                      <div key={eIdx} style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: 'var(--color-muted)' }}>{econ.strategy}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 600, fontFamily: 'monospace', color: econ.netCreditDebit >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)' }}>
                              {econ.netCreditDebit >= 0 ? '+' : ''}${econ.netCreditDebit.toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => onSelectRoll?.({ strategy: econ.strategy, strike: econ.strike, expiry: econ.expiry, premium: econ.newBid })}
                              style={{ padding: '0.125rem 0.5rem', fontSize: '0.7rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '3px', cursor: 'pointer', minHeight: 'auto' }}
                            >
                              Roll
                            </button>
                          </div>
                        </div>
                        <p style={{ margin: '0.125rem 0 0', fontSize: '0.75rem', color: 'var(--color-muted)' }}>{econ.description}</p>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>


    </section>
  );
}

/** Formats a price to 3dp if the 3rd decimal is non-zero, otherwise 2dp. */
function formatPrice(value: number): string {
  const thirdDecimal = Math.round((value * 1000) % 10);
  return thirdDecimal !== 0 ? value.toFixed(3) : value.toFixed(2);
}

/** Formats a timestamp as relative time (e.g., "2 min ago", "1 hr ago"). */
function formatRelativeTime(timestamp: string): string {
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;

  return new Date(timestamp).toLocaleDateString();
}

/**
 * Determines if a timestamp falls within the current ASX trading day (10:00-16:00 AEST weekdays).
 */
function isWithinTradingDay(timestamp: string): boolean {
  const date = new Date(timestamp);
  const now = new Date();

  // Check if it's the same calendar day in Sydney timezone
  const formatter = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' });
  const timestampDay = formatter.format(date);
  const todayDay = formatter.format(now);

  return timestampDay === todayDay;
}

/** Data source badge: "Live (IBKR)" in blue or "Manual" in grey. */
function DataSourceBadge({ isPriceManual, lastPriceTimestamp }: { isPriceManual?: boolean; lastPriceTimestamp?: string }) {
  if (!lastPriceTimestamp) return null;

  const isLive = !isPriceManual && isWithinTradingDay(lastPriceTimestamp);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.125rem 0.5rem',
        borderRadius: '12px',
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: isLive ? 'var(--color-brand-fill)' : 'var(--color-surface-2)',
        color: isLive ? 'var(--color-link)' : 'var(--color-muted)',
      }}
    >
      {isLive ? 'Live (IBKR)' : 'Manual'}
    </span>
  );
}

/** Informational stock-gain display value for the above-strike covered-call state. */
export interface StockGainSplit {
  /**
   * The capped gain the written call limits the shares to:
   * (min(lastStockPrice, strikePrice) − purchasePrice) × quantity, rounded to 2dp.
   * Informational only — NOT added to the P&L total; the full "Total stock gain"
   * (the breakdown's own stockGainLoss) is what feeds Net P&L / Mark-to-market.
   */
  toStrike: number;
}

/**
 * Computes the informational capped ("to strike") stock-gain value shown above
 * the full "Total stock gain" line when a written call is active and the stock
 * has risen above the strike.
 *
 * Caller must only invoke this in the gated state (active call, above strike),
 * where lastStockPrice and the strike are both defined and lastStockPrice > strike.
 *
 * This value is display-only and is NOT summed into any total: the written call
 * caps the shares' gain at the strike, so this line tells the user what that cap
 * is, while the full stock gain is shown separately (and is the figure that
 * drives the headline totals).
 */
export function splitStockGain(
  lastStockPrice: number,
  strikePrice: number,
  purchasePrice: number,
  quantity: number,
): StockGainSplit {
  const toStrike = roundTo2dp((Math.min(lastStockPrice, strikePrice) - purchasePrice) * quantity);
  return { toStrike };
}

/** Helper: displays a label-value detail pair. */
export function DetailItem({ label, value, info }: { label: string; value: string; info?: string }) {
  return (
    <div>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        {info && <FieldInfo label={label} tip={info} />}
      </span>
      <div style={{ fontWeight: 500 }}>{value}</div>
    </div>
  );
}

/** Helper: displays a P&L line item with conditional coloring. */
export function PnLLineItem({ label, value, isBold, isDeduction, info }: { label: string; value: number; isBold?: boolean; isDeduction?: boolean; info?: string }) {
  const displayValue = isDeduction ? value : value;
  const color = displayValue > 0 ? 'var(--color-pnl-positive)' : displayValue < 0 ? 'var(--color-pnl-negative)' : 'var(--color-text)';

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: isBold ? 700 : 400 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        <span>{label}</span>
        {info && <FieldInfo label={label} tip={info} />}
      </span>
      <span style={{ color, fontFamily: 'monospace' }}>
        {displayValue >= 0 ? '+' : ''}{displayValue.toFixed(2)}
      </span>
    </div>
  );
}
